import { useEffect, useMemo } from 'react';
import { Button, InlineNotification } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { useHistory } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import { useWizardStep } from 'components/full-page-wizard';
import { SplitPanel } from 'components/split-panel';
import {
  SlotAllocationsSummary,
  SlotSummaryRow,
} from 'components/slot-summary-table';
import { RelatedItemsSection } from 'components/related-items';
import { newWorkspacePath } from 'pages/title-support-workspaces/api';
import { TitleSupportWorkspacesTable } from 'pages/title-support-workspaces/page';
import routes from '~/routes';
import {
  activeTractIdLoadableAtom,
  bundleOfSticksDetailsLoadableAtom,
  crmCreatePermissionLoadableAtom,
  createdSubtractDetailsLoadableAtom,
  createdTractDetailsFailedAtom,
  createdTractDetailsLoadableAtom,
  tractSummaryDataLoadableAtom,
} from './atoms';
import { BundleOfSticksTable } from './bundle-of-sticks-table';
import {
  buildInterestConversionData,
  createEmptyInterestConversionData,
} from './bundle-of-sticks-utils';
import { useNRIReviewData, useTractIntersectionRequestAnalysis } from './hooks';
import { TractMap } from './tract-map';
import { RelatedPackagesTable } from './tract-summary/related-packages-table';
import { SlotAllocationsTable } from './tract-summary/slot-allocations-table';
import { SlotsTable } from './tract-summary/slots-table';
import {
  SummaryTopSection,
  type SummaryEntityData,
} from './tract-summary/summary-top-section';
import { formatTitleRiskValue } from './utils';
import style from './tract-summary.module.scss';
import { wellStatusCounts } from 'utils/slot-categories';

/**
 * Default `summaryData` payload while `tractSummaryDataLoadableAtom`
 * is still loading. Matches the atom's resolved shape so the
 * `<SummaryTopSection>` consumer doesn't need a loading branch.
 */
const EMPTY_SUMMARY_DATA: SummaryEntityData = {
  entity: undefined,
  account: undefined,
  entities: [],
  insightsEntityDisplay: { type: 'singleEntity', entity: undefined },
  showEntityDetails: false,
  hasMultipleEntities: false,
};

/**
 * CRM Tract Wizard's Summary step. Two-pane SplitPanel layout: left
 * holds a top 3-column grid (Tract / Subtract / Entity & CRM) followed
 * by a Slot Summary table and three collapsible accordion sections
 * (Bundle of Sticks Ownership, Slots, Slot Allocations); right holds
 * the tract map. As the last step in the wizard, "Next" navigates back
 * to the CRM tracts list rather than advancing — wired via
 * `useWizardStep().setOnNext`. Navigation guard is disabled because
 * the summary is read-only.
 *
 * @return {JSX.Element} The summary step.
 */
export const TractSummary = (): JSX.Element => {
  const { goToStep, setOnNext, setNavigationGuard, finishWizard } =
    useWizardStep();
  const history = useHistory();

  const summaryDataLoadable = useAtomValue(tractSummaryDataLoadableAtom);
  const tractDetailsLoadable = useAtomValue(createdTractDetailsLoadableAtom);
  const tractDetailsFailed = useAtomValue(createdTractDetailsFailedAtom);
  const subtractDetailsLoadable = useAtomValue(
    createdSubtractDetailsLoadableAtom
  );
  const crmCreatePermission = useAtomValue(crmCreatePermissionLoadableAtom);
  const bosLoadable = useAtomValue(bundleOfSticksDetailsLoadableAtom);
  const activeTractIdLoadable = useAtomValue(activeTractIdLoadableAtom);
  const activeTractId =
    activeTractIdLoadable.state === 'hasData'
      ? activeTractIdLoadable.data
      : null;

  const {
    futureDevelopmentTractIntersectionAnalysis: tractIntersectionAnalysis,
    isLoading: isTIALoading,
  } = useTractIntersectionRequestAnalysis();
  const { isLoading: isNRILoading, tractNRI, slotNRI } = useNRIReviewData();

  // Last-step navigation: "Next" button navigates back to the CRM
  // tracts list instead of advancing. Without this, the button would
  // be a no-op (FullPageWizard's `useStepper` doesn't auto-advance on
  // the last step). Guard is disabled because the summary is
  // read-only — no unsaved changes possible.
  useEffect(() => {
    setNavigationGuard(false);
    setOnNext(() => {
      finishWizard(routes.crmTractsAndSubtractsPath);
    });
  }, [finishWizard, setNavigationGuard, setOnNext]);

  const tractDetails =
    tractDetailsLoadable.state === 'hasData' ? tractDetailsLoadable.data : null;
  const subtractDetails =
    subtractDetailsLoadable.state === 'hasData'
      ? subtractDetailsLoadable.data
      : null;
  const hasCrmCreatePermission =
    crmCreatePermission.state === 'hasData' &&
    crmCreatePermission.data?.hasPermissions;
  const summaryData =
    summaryDataLoadable.state === 'hasData'
      ? summaryDataLoadable.data
      : EMPTY_SUMMARY_DATA;

  const slotSummary: SlotSummaryRow[] = useMemo(
    () =>
      tractIntersectionAnalysis?.slotsSummary?.rows?.map((row) => ({
        total: row.total,
        wellType: row.unitType,
        producing: row.pdp,
        duc: row.duc,
        permit: row.permit,
        ltd: row.ltd,
        shutIn: row.shutIn,
        inactive: row.inactive,
        ...wellStatusCounts(row),
        expiredPermit: row.expiredPermit,
        wellboreExcluded: row.wellboreExcluded ?? 0,
      })) ?? [],
    [tractIntersectionAnalysis?.slotsSummary?.rows]
  );

  const isBosLoading = bosLoadable.state === 'loading';
  const bosData = bosLoadable.state === 'hasData' ? bosLoadable.data : null;
  const interestConversionData = useMemo(() => {
    if (!bosData) return createEmptyInterestConversionData();
    return buildInterestConversionData(bosData);
  }, [bosData]);
  // Every package containing this tract, from crm/tracts#show. The section
  // must never render a failure as a confident "this tract is in no
  // packages", so it reads the two signals that can actually occur — neither
  // of which reaches the loadable's `hasError`:
  //
  //  - the request failed: `request.get` resolves an `{ error }` object
  //    rather than throwing, so the loadable lands in `hasData` with null
  //    data — which is also its idle state, hence the dedicated atom;
  //  - Elasticsearch failed behind a 200: `Query::Packages#results` degrades
  //    to [], which the payload flags as `relatedPackagesFailed`.
  //
  // Only a resolved response carrying real data with no packages — a tract
  // being created — omits the section.
  const relatedPackages = tractDetails?.relatedPackages ?? [];
  const isTractDetailsLoading = tractDetailsLoadable.state === 'loading';
  const hasRelatedPackagesError =
    tractDetailsFailed || (tractDetails?.relatedPackagesFailed ?? false);
  const showRelatedPackages =
    isTractDetailsLoading ||
    hasRelatedPackagesError ||
    relatedPackages.length > 0;
  const isRelatedPackagesTruncated =
    tractDetails?.relatedPackagesTruncated ?? false;

  const tractDetailsInstrumentType = tractDetails?.assetType ?? null;
  const instrumentType = bosData?.instrumentType ?? tractDetailsInstrumentType;

  // Look up THIS subtract's risk by id rather than blindly taking the
  // first entry — a tract can hold multiple subtracts.
  const subtractRisk = tractDetails?.subtracts?.find(
    (s) => s.id === subtractDetails?.id
  );
  const hasRiskApplied = Boolean(
    subtractRisk &&
    ((subtractRisk.titleQuality && subtractRisk.titleQuality !== 'none') ||
      (subtractRisk.titleRiskFactor && subtractRisk.titleRiskFactor !== 'none'))
  );

  return (
    <SplitPanel
      left={
        <div>
          <span className={style.title}>Summary</span>
          {hasRiskApplied && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title="Title Assessment"
              subtitle={`Title Quality: ${formatTitleRiskValue(
                subtractRisk?.titleQuality
              )} · Title Risk Factor: ${formatTitleRiskValue(
                subtractRisk?.titleRiskFactor
              )}`}
            />
          )}
          <div className={style.topSection}>
            <SummaryTopSection
              tract={tractDetails}
              subtract={subtractDetails}
              summaryData={summaryData}
              onLinkInsightsEntity={() => goToStep(1)}
              disableLinkInsightsEntity={!hasCrmCreatePermission}
            />
          </div>
          <Accordion defaultExpanded>
            <AccordionSummary>Bundle of Sticks Ownership</AccordionSummary>
            <AccordionDetails className={style.accordionDetailsOverride}>
              {!isBosLoading && !bosData ? (
                <div className={style.noDataMessage}>
                  No Bundle of Sticks ownership data available for this
                  subtract.
                </div>
              ) : (
                <div className={style.accordionTableContent}>
                  <BundleOfSticksTable
                    data={interestConversionData}
                    loading={isBosLoading}
                    instrumentType={instrumentType}
                  />
                </div>
              )}
            </AccordionDetails>
          </Accordion>
          <div className={style.slotSummary}>
            <SlotAllocationsSummary
              slotSummary={slotSummary}
              slots={tractNRI ?? []}
              isLoading={isTIALoading || isNRILoading}
              showHighlight
              tableId="crm-tract-summary-slot-allocations"
            />
          </div>
          <Accordion defaultExpanded>
            <AccordionSummary>Slots</AccordionSummary>
            <AccordionDetails className={style.accordionDetailsOverride}>
              <div className={style.accordionTableFlex}>
                <SlotsTable rows={slotNRI ?? []} isLoading={isNRILoading} />
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>Slot Allocations</AccordionSummary>
            <AccordionDetails className={style.accordionDetailsOverride}>
              <div className={style.accordionTableFlex}>
                <SlotAllocationsTable
                  rows={tractNRI ?? []}
                  isLoading={isNRILoading}
                />
              </div>
            </AccordionDetails>
          </Accordion>
          {showRelatedPackages && (
            <Accordion defaultExpanded>
              <AccordionSummary>Related Packages</AccordionSummary>
              <AccordionDetails className={style.accordionDetailsOverride}>
                {hasRelatedPackagesError ? (
                  <div className={style.noDataMessage}>
                    Related packages could not be loaded for this tract.
                  </div>
                ) : (
                  <>
                    {isRelatedPackagesTruncated && (
                      <InlineNotification
                        kind="info"
                        lowContrast
                        hideCloseButton
                        title="Package list truncated"
                        subtitle="This tract is contained in more packages than can be shown; only the most recently created ones are listed."
                      />
                    )}
                    <div className={style.accordionTableFlex}>
                      <RelatedPackagesTable
                        rows={relatedPackages}
                        isLoading={isTractDetailsLoading}
                      />
                    </div>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          {activeTractId && (
            <Accordion defaultExpanded>
              <AccordionSummary>Title Support Workspaces</AccordionSummary>
              <AccordionDetails className={style.accordionDetailsOverride}>
                <div className={style.accordionTableFlex}>
                  <TitleSupportWorkspacesTable
                    filters={{ salesforce_tract_id: activeTractId }}
                    toolbarAction={
                      tractDetails?.name && (
                        <Button
                          key="create"
                          size="lg"
                          renderIcon={Add}
                          onClick={() =>
                            history.push(
                              newWorkspacePath({
                                id: activeTractId,
                                name: tractDetails.name,
                              })
                            )
                          }
                        >
                          Create Workspace
                        </Button>
                      )
                    }
                  />
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          {/* Read-only twin of the Tract step's list (INS-10826). Links hang off a Salesforce Id, so create mode has nothing to fetch. */}
          {activeTractId && (
            <RelatedItemsSection
              host={{
                basePath: `/crm/tracts/${activeTractId}`,
                notifications: tractDetails?.notifications,
              }}
              readOnly
              // No card around this one, so the section's own header carries the
              // marker — otherwise the summary reads as a table that simply has no
              // actions rather than one that is deliberately view-only.
              showReadOnlyTag
            />
          )}
        </div>
      }
      right={<TractMap />}
    />
  );
};
